import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';

type LoginRole = 'investor' | 'entrepreneur';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [localLoading, setLocalLoading] = useState(false);
  const [role, setRole] = useState<LoginRole>('investor');
  const { signIn, signInWithGoogle, isLoading: authLoading, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get the return path from location state
  const from = (location.state as { from?: string })?.from;
  const initialEmail = (location.state as { email?: string })?.email;

  // Reset loading state when auth state changes
  useEffect(() => {
    const timer = setTimeout(() => {
      if (authLoading === false) {
        setLocalLoading(false);
      }
    }, 100); // Small delay to prevent rapid state changes

    return () => clearTimeout(timer);
  }, [authLoading]);

  // Set initial email if provided in location state
  useEffect(() => {
    if (initialEmail) {
      setEmail(initialEmail);
    }
  }, [initialEmail]);

  // Redirect if already logged in - use role redirect
  useEffect(() => {
    if (user) {
      // If there's a specific page the user was trying to access, go there
      if (from) {
        navigate(from);
        return;
      }
      
      // Use the login/redirect route which will handle role-based redirection
      navigate('/login/redirect');
    }
  }, [user, navigate, from]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      setLocalLoading(true);
      await signIn(email, password);
      // Don't reset loading here - let the auth state change handle it
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Invalid email or password');
      setLocalLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setLocalLoading(true);
      await signInWithGoogle(role);
      // Don't reset loading here - let the auth state change handle it
    } catch (error) {
      console.error('Google sign-in error:', error);
      toast.error('Failed to sign in with Google');
      setLocalLoading(false);
    }
  };

  // Only disable the form when we're actually in a loading state
  const isFormDisabled = localLoading;

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex justify-center mb-4">
            <Link to="/" className="flex items-center">
              <img src="/uploads/5fd4894b-684c-481f-92ad-df1c1aa2d9db.png" alt="H44S Logo" className="h-[60px] w-auto" />
            </Link>
          </div>
          <CardTitle className="text-2xl text-center">Login to your account</CardTitle>
          <CardDescription className="text-center">
            Choose your login method
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Role Selection */}
          <div className="space-y-3">
            <label className="text-sm font-medium">I am logging in as a:</label>
            <RadioGroup 
              value={role} 
              onValueChange={(value) => setRole(value as LoginRole)}
              className="flex flex-col space-y-2"
              disabled={isFormDisabled}
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="entrepreneur" id="entrepreneur" />
                <Label htmlFor="entrepreneur">Entrepreneur</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="investor" id="investor" />
                <Label htmlFor="investor">Investor</Label>
              </div>
            </RadioGroup>
          </div>

          {/* Google Sign In Button */}
          <Button
            type="button"
            variant="outline"
            className="w-full flex items-center justify-center gap-2"
            onClick={handleGoogleSignIn}
            disabled={isFormDisabled}
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            {localLoading ? 'Signing in...' : 'Continue with Google'}
          </Button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">
                Or continue with email
              </span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium">Email</label>
              <Input 
                id="email" 
                type="email" 
                placeholder="name@example.com" 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isFormDisabled}
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label htmlFor="password" className="text-sm font-medium">Password</label>
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-h44s-purple hover:underline"
                  tabIndex={isFormDisabled ? -1 : undefined}
                >
                  Forgot password?
                </Link>
              </div>
              <Input 
                id="password" 
                type="password" 
                placeholder="••••••••" 
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isFormDisabled}
              />
            </div>

            <Button 
              type="submit" 
              className="w-full bg-h44s-purple hover:bg-h44s-purple/90"
              disabled={isFormDisabled}
            >
              {localLoading ? 'Logging in...' : 'Login with Email'}
            </Button>
          </form>
        </CardContent>

        <CardFooter className="flex flex-col space-y-4">
          <div className="text-center text-sm">
            Don't have an account?{' '}
            <Link 
              to="/signup" 
              className="text-h44s-purple hover:underline"
              tabIndex={isFormDisabled ? -1 : undefined}
            >
              Sign up
            </Link>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
};

export default Login;
